<template>
  <div class="user-management-container">
    <div class="page-header">
      <h1>사용자 관리</h1>
      <button class="add-user-btn" @click="openAddUserModal">
        <i class="add-icon">➕</i> 사용자 추가
      </button>
    </div>
    
    <div class="users-table-container">
      <table class="users-table">
        <thead>
          <tr>
            <th>아이디(이메일)</th>
            <th>이름</th>
            <th>권한</th>
            <th>상태</th>
            <th>전화번호</th>
            <th>휴대폰</th>
            <th>마지막 로그인</th>
            <th>등록일</th>
            <th>작업</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="isLoading">
            <td colspan="9" class="loading-message">
              <div class="loading-spinner"></div>
              사용자 정보를 불러오는 중...
            </td>
          </tr>
          <tr v-else-if="users.length === 0">
            <td colspan="9" class="empty-message">
              등록된 사용자가 없습니다.
            </td>
          </tr>
          <tr v-for="user in users" :key="user.user_id" class="user-row">
            <td>{{ user.user_id }}</td>
            <td>{{ user.name }}</td>
            <td>{{ formatGrade(user.grade) }}</td>
            <td>{{ formatStatus(user.status) }}</td>
            <td>{{ formatPhoneNumber(user.tel) }}</td>
            <td>{{ formatPhoneNumber(user.phone) }}</td>
            <td>{{ formatDate(user.last_login_date) }}</td>
            <td>{{ formatDate(user.reg_date) }}</td>
            <td class="actions">
              <button class="action-btn edit-btn" @click="editUser(user)">수정</button>
              <button class="action-btn delete-btn" @click="confirmDeleteUser(user)">탈퇴</button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    
    <!-- 사용자 추가/수정 모달 -->
    <div class="modal-overlay" v-if="showModal" @click="closeModal"></div>
    <div class="modal" v-if="showModal">
      <div class="modal-header">
        <h2>{{ isEditMode ? '사용자 정보 수정' : '사용자 추가' }}</h2>
        <button class="close-btn" @click="closeModal">&times;</button>
      </div>
      <div class="modal-body">
        <form @submit.prevent="saveUser" class="user-form">
          <div class="form-group">
            <label for="user_id">아이디(이메일) *</label>
            <input 
              type="email" 
              id="user_id" 
              v-model="userForm.user_id" 
              required
              :disabled="isEditMode"
              placeholder="example@email.com"
            >
          </div>
          
          <div class="form-group" v-if="!isEditMode">
            <label for="password">비밀번호 *</label>
            <input 
              type="password" 
              id="password" 
              v-model="userForm.password" 
              required
              placeholder="비밀번호를 입력하세요"
            >
          </div>
          
          <div class="form-group" v-if="isEditMode">
            <label for="password">비밀번호 (변경 시에만 입력)</label>
            <input 
              type="password" 
              id="password" 
              v-model="userForm.password" 
              placeholder="비밀번호를 변경하려면 입력하세요"
            >
            <small class="help-text">비워두면 기존 비밀번호가 유지됩니다.</small>
          </div>
          
          <div class="form-group">
            <label for="name">이름 *</label>
            <input 
              type="text" 
              id="name" 
              v-model="userForm.name" 
              required
              placeholder="이름을 입력하세요"
            >
          </div>
          
          <div class="form-group">
            <label for="grade">권한 *</label>
            <select id="grade" v-model="userForm.grade" required>
              <option value="0">최고관리자</option>
              <option value="1">계정관리자</option>
              <option value="2">일반사용자</option>
            </select>
          </div>
          
          <div class="form-group">
            <label for="status">상태 *</label>
            <select id="status" v-model="userForm.status" required>
              <option value="1">활성</option>
              <option value="2">휴면</option>
              <option value="3">탈퇴</option>
            </select>
          </div>
          
          <div class="form-group">
            <label for="phone">휴대폰 번호</label>
            <input 
              type="text" 
              id="phone" 
              v-model="userForm.phone"
              @input="formatPhoneInput('phone')"
              placeholder="010-0000-0000"
            >
          </div>
          
          <div class="form-group">
            <label for="tel">전화번호</label>
            <input 
              type="text" 
              id="tel" 
              v-model="userForm.tel"
              @input="formatPhoneInput('tel')"
              placeholder="02-0000-0000"
            >
          </div>
          
          <div class="form-actions">
            <button type="button" class="cancel-btn" @click="closeModal">취소</button>
            <button type="submit" class="save-btn">저장</button>
          </div>
        </form>
      </div>
    </div>
    
    <!-- 삭제 확인 모달 -->
    <div class="modal-overlay" v-if="showDeleteConfirm" @click="showDeleteConfirm = false"></div>
    <div class="modal confirm-modal" v-if="showDeleteConfirm">
      <div class="modal-header">
        <h2>사용자 탈퇴</h2>
        <button class="close-btn" @click="showDeleteConfirm = false">&times;</button>
      </div>
      <div class="modal-body">
        <p><strong>{{ selectedUser?.name }}</strong> 사용자를 탈퇴 처리하시겠습니까?</p>
        <p class="warning">이 작업은 되돌릴 수 없습니다.</p>
        
        <div class="form-actions">
          <button type="button" class="cancel-btn" @click="showDeleteConfirm = false">취소</button>
          <button type="button" class="delete-confirm-btn" @click="deleteUser">탈퇴</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, defineComponent } from 'vue';
import axios from 'axios';
import { swalAlert } from '@/utils/swal';
import { isSuperAdmin } from '@/utils/user-auth';

defineComponent({
  name: 'UserManagement'
});

// 상태 변수
const users = ref([]);
const isLoading = ref(true);
const showModal = ref(false);
const showDeleteConfirm = ref(false);
const isEditMode = ref(false);
const selectedUser = ref(null);

// 사용자 폼 데이터
const userForm = ref({
  user_id: '',
  password: '',
  name: '',
  grade: '2', // 기본값: 일반사용자
  status: '1', // 기본값: 활성
  phone: '',
  tel: ''
});

// 사용자 목록 가져오기
const fetchUsers = async () => {
  try {
    isLoading.value = true;
    const response = await axios.get('/nosp/users');
    if (response.data && response.data.status === 'success') {
      users.value = response.data.users || [];
    } else {
      swalAlert('사용자 정보를 불러오는데 실패했습니다.', 'error');
    }
  } catch (error) {
    console.error('사용자 목록 조회 오류:', error);
    swalAlert('사용자 정보를 불러오는데 실패했습니다.', 'error');
  } finally {
    isLoading.value = false;
  }
};

// 권한 텍스트 포맷
const formatGrade = (grade) => {
  switch (grade) {
    case '0': return '최고관리자';
    case '1': return '계정관리자';
    case '2': return '일반사용자';
    default: return '일반사용자';
  }
};

// 상태 텍스트 포맷
const formatStatus = (status) => {
  switch (status) {
    case '1': return '활성';
    case '2': return '휴면';
    case '3': return '탈퇴';
    default: return '활성';
  }
};

// 날짜 포맷
const formatDate = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString;
  
  return date.toLocaleString('ko-KR', { 
    year: 'numeric', 
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// 전화번호 포맷
const formatPhoneNumber = (phoneNumber) => {
  if (!phoneNumber) return '-';
  
  // 전화번호 형식 변환 (01012345678 -> 010-1234-5678)
  const cleaned = phoneNumber.replace(/\D/g, '');
  
  if (cleaned.length === 11) { // 휴대폰 번호
    return cleaned.replace(/(\d{3})(\d{4})(\d{4})/, '$1-$2-$3');
  } else if (cleaned.length === 10) { // 일반 전화번호(지역번호 2자리)
    return cleaned.replace(/(\d{2})(\d{4})(\d{4})/, '$1-$2-$3');
  } else if (cleaned.length === 9) { // 일반 전화번호(지역번호 2자리)
    return cleaned.replace(/(\d{2})(\d{3})(\d{4})/, '$1-$2-$3');
  } else {
    return phoneNumber;
  }
};

// 전화번호 입력 시 자동 포맷팅
const formatPhoneInput = (fieldName) => {
  const value = userForm.value[fieldName];
  if (!value) return;
  
  // 숫자와 하이픈만 남기고 제거
  let cleaned = value.replace(/[^\d-]/g, '');
  // 하이픈 제거 후 숫자만 남김
  cleaned = cleaned.replace(/-/g, '');
  
  if (cleaned.length <= 3) {
    userForm.value[fieldName] = cleaned;
  } else if (cleaned.length <= 7) {
    userForm.value[fieldName] = cleaned.replace(/(\d{3})(\d{1,4})/, '$1-$2');
  } else {
    userForm.value[fieldName] = cleaned.replace(/(\d{3})(\d{4})(\d{1,4})/, '$1-$2-$3');
  }
};

// 사용자 추가 모달 열기
const openAddUserModal = () => {
  isEditMode.value = false;
  userForm.value = {
    user_id: '',
    password: '',
    name: '',
    grade: '2',
    status: '1',
    phone: '',
    tel: ''
  };
  showModal.value = true;
};

// 사용자 수정 모달 열기
const editUser = (user) => {
  isEditMode.value = true;
  selectedUser.value = user;
  userForm.value = {
    user_id: user.user_id,
    password: '', // 비밀번호는 표시하지 않음
    name: user.name,
    grade: user.grade,
    status: user.status,
    phone: user.phone || '',
    tel: user.tel || ''
  };
  showModal.value = true;
};

// 모달 닫기
const closeModal = () => {
  showModal.value = false;
};

// 사용자 저장(추가/수정)
const saveUser = async () => {
  try {
    // 이메일 유효성 검사
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(userForm.value.user_id)) {
      swalAlert('유효한 이메일 주소를 입력해주세요.', 'warning');
      return;
    }
    
    // 전화번호/휴대폰 번호 유효성 검사
    if (userForm.value.phone && !userForm.value.phone.match(/^\d{3}-\d{3,4}-\d{4}$/)) {
      swalAlert('휴대폰 번호 형식이 올바르지 않습니다. (예: 010-1234-5678)', 'warning');
      return;
    }
    
    if (userForm.value.tel && !userForm.value.tel.match(/^\d{2,3}-\d{3,4}-\d{4}$/)) {
      swalAlert('전화번호 형식이 올바르지 않습니다. (예: 02-123-4567)', 'warning');
      return;
    }
    
    // 권한 확인 - 최고관리자만 다른 최고관리자 생성/수정 가능
    if (userForm.value.grade === '0' && !isSuperAdmin()) {
      swalAlert('최고관리자 권한을 부여할 수 없습니다.', 'warning');
      return;
    }
    
    if (isEditMode.value) {
      // 사용자 정보 수정
      const response = await axios.put(`/nosp/users/${userForm.value.user_id}`, userForm.value);
      if (response.data && response.data.status === 'success') {
        swalAlert('사용자 정보가 수정되었습니다.', 'success');
        fetchUsers();
        closeModal();
      } else {
        swalAlert(response.data.message || '사용자 정보 수정에 실패했습니다.', 'error');
      }
    } else {
      // 새 사용자 추가
      const response = await axios.post('/nosp/users', userForm.value);
      if (response.data && response.data.status === 'success') {
        swalAlert('사용자가 추가되었습니다.', 'success');
        fetchUsers();
        closeModal();
      } else {
        swalAlert(response.data.message || '사용자 추가에 실패했습니다.', 'error');
      }
    }
  } catch (error) {
    console.error('사용자 저장 오류:', error);
    swalAlert('처리 중 오류가 발생했습니다.', 'error');
  }
};

// 사용자 삭제 확인 모달 열기
const confirmDeleteUser = (user) => {
  selectedUser.value = user;
  showDeleteConfirm.value = true;
};

// 사용자 삭제
const deleteUser = async () => {
  try {
    // 최고관리자는 최고관리자만 삭제 가능
    if (selectedUser.value.grade === '0' && !isSuperAdmin()) {
      swalAlert('최고관리자는 탈퇴 처리할 수 없습니다.', 'warning');
      showDeleteConfirm.value = false;
      return;
    }
    
    const response = await axios.delete(`/nosp/users/${selectedUser.value.user_id}`);
    if (response.data && response.data.status === 'success') {
      swalAlert('사용자가 탈퇴 처리되었습니다.', 'success');
      fetchUsers();
      showDeleteConfirm.value = false;
    } else {
      swalAlert(response.data.message || '사용자 탈퇴 처리에 실패했습니다.', 'error');
    }
  } catch (error) {
    console.error('사용자 탈퇴 처리 오류:', error);
    swalAlert('처리 중 오류가 발생했습니다.', 'error');
  }
};

onMounted(() => {
  // 페이지 로드 시 사용자 목록 가져오기
  fetchUsers();
});
</script>

<style scoped>
.user-management-container {
  padding: 24px;
  max-width: 1400px;
  margin: 0 auto;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.page-header h1 {
  font-size: 24px;
  margin: 0;
  color: #333;
}

.add-user-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: #1a73e8;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.3s;
}

.add-user-btn:hover {
  background-color: #1765cc;
}

.add-icon {
  font-size: 16px;
}

/* 테이블 스타일 */
.users-table-container {
  overflow-x: auto;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
}

.users-table {
  width: 100%;
  border-collapse: collapse;
}

.users-table th,
.users-table td {
  padding: 12px 16px;
  text-align: left;
  border-bottom: 1px solid #eee;
}

.users-table th {
  font-weight: 600;
  color: #444;
  background-color: #f9fafb;
}

.user-row:hover {
  background-color: #f9fafb;
}

.loading-message,
.empty-message {
  text-align: center;
  padding: 32px;
  color: #666;
}

.loading-spinner {
  display: inline-block;
  width: 24px;
  height: 24px;
  margin-right: 8px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #1a73e8;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.actions {
  display: flex;
  gap: 8px;
}

.action-btn {
  padding: 6px 12px;
  border: none;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.3s;
}

.edit-btn {
  background-color: #f9f9f9;
  color: #1a73e8;
  border: 1px solid #dee2e6;
}

.edit-btn:hover {
  background-color: #f0f0f0;
}

.delete-btn {
  background-color: #fff2f2;
  color: #e53935;
  border: 1px solid #ffcdd2;
}

.delete-btn:hover {
  background-color: #ffeaea;
}

/* 모달 스타일 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 100;
}

.modal {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 101;
  background-color: white;
  border-radius: 8px;
  width: 500px;
  max-width: 90%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

.confirm-modal {
  width: 400px;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  border-bottom: 1px solid #eee;
}

.modal-header h2 {
  margin: 0;
  font-size: 18px;
  color: #333;
}

.close-btn {
  background: none;
  border: none;
  font-size: 24px;
  color: #666;
  cursor: pointer;
}

.modal-body {
  padding: 24px;
}

/* 폼 스타일 */
.user-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.form-group label {
  font-size: 14px;
  font-weight: 500;
  color: #444;
}

.form-group input,
.form-group select {
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
}

.form-group input:focus,
.form-group select:focus {
  outline: none;
  border-color: #1a73e8;
  box-shadow: 0 0 0 2px rgba(26, 115, 232, 0.1);
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 16px;
}

.cancel-btn,
.save-btn,
.delete-confirm-btn {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s;
}

.cancel-btn {
  background-color: #f5f5f5;
  color: #555;
}

.cancel-btn:hover {
  background-color: #eee;
}

.save-btn {
  background-color: #1a73e8;
  color: white;
}

.save-btn:hover {
  background-color: #1765cc;
}

.delete-confirm-btn {
  background-color: #e53935;
  color: white;
}

.delete-confirm-btn:hover {
  background-color: #d32f2f;
}

.warning {
  color: #e53935;
  font-size: 14px;
}

.help-text {
  font-size: 12px;
  color: #666;
  margin-top: 4px;
}

@media (max-width: 768px) {
  .user-management-container {
    padding: 16px;
  }
  
  .page-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
  
  .add-user-btn {
    width: 100%;
    justify-content: center;
  }
  
  .users-table th,
  .users-table td {
    padding: 8px;
    font-size: 13px;
  }
  
  .action-btn {
    padding: 4px 8px;
    font-size: 11px;
  }
}
</style> 